import databaseService from './database.service';

/**
 * Quotation Service for ZyloBill Web App
 * Adapted from desktop app to work with browser PouchDB
 */

export const quotationService = {
  /* ═══════════════════════════════════════════════════════════
     QUOTATION LIFECYCLE
     ═══════════════════════════════════════════════════════════ */

  /**
   * Create new quotation (draft)
   */
  async create(data) {
    const quotationId = `quotation_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const quotation = {
      _id: quotationId,
      type: 'quotation',
      quotationType: data.type || 'goods',
      status: 'draft',
      quote_number: data.quote_number || await this.generateQuoteNumber(),
      issue_date: data.issue_date || new Date().toISOString().slice(0, 10),
      valid_until: data.valid_until || this.getDefaultValidUntil(),
      client_id: null,
      items: [],
      discount: 0,
      discount_type: 'flat',
      shipping: 0,
      round_off: 0,
      subtotal: 0,
      tax_total: 0,
      total: 0,
      notes: '',
      deleted: false,
    };

    const result = await databaseService.createDocument(quotation);
    
    return {
      id: result.id,
      quote_number: quotation.quote_number,
      ...quotation
    };
  },

  /**
   * Generate next quote number
   */
  async generateQuoteNumber() {
    const quotations = await databaseService.getQuotations();
    const numbers = quotations
      .map(quot => {
        const match = quot.quote_number?.match(/QUO-(\d+)/);
        return match ? parseInt(match[1]) : 0;
      })
      .filter(n => n > 0);
    
    const nextNumber = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
    return `QUO-${String(nextNumber).padStart(4, '0')}`;
  },

  /**
   * Get default valid until date (30 days from now)
   */
  getDefaultValidUntil() {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().slice(0, 10);
  },

  /**
   * Autosave quotation (main save function)
   * Handles header + items + calculations
   */
  async save(quotationId, data) {
    const currentQuotation = await databaseService.readDocument(quotationId);
    
    if (!currentQuotation) {
      throw new Error('Quotation not found');
    }

    // Calculate totals
    const calculations = this.calculateTotals(data.items || [], {
      discount: data.discount || 0,
      discountType: data.discountType || 'flat',
      shipping: data.shipping || 0,
      roundOff: data.roundOff || 0,
    });

    const updatedQuotation = {
      ...currentQuotation,
      quote_number: data.quoteNumber || currentQuotation.quote_number,
      quotationType: data.type || currentQuotation.quotationType,
      client_id: data.client?.id || currentQuotation.client_id,
      client_name: data.client?.name || currentQuotation.client_name,
      client_email: data.client?.email || currentQuotation.client_email,
      client_phone: data.client?.phone || currentQuotation.client_phone,
      client_gstin: data.client?.gstin || currentQuotation.client_gstin,
      client_billing_address: data.client?.billing_address || currentQuotation.client_billing_address,
      client_city: data.client?.city || currentQuotation.client_city,
      client_state: data.client?.state || currentQuotation.client_state,
      client_pincode: data.client?.pincode || currentQuotation.client_pincode,
      issue_date: data.issueDate || currentQuotation.issue_date,
      valid_until: data.validUntil || currentQuotation.valid_until,
      discount: data.discount || 0,
      discount_type: data.discountType || 'flat',
      shipping: data.shipping || 0,
      round_off: data.roundOff || 0,
      items: (data.items || []).map(item => ({
        id: item.id || `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: item.name,
        description: item.description || '',
        type: item.type || 'goods',
        quantity: item.quantity || 1,
        rate: item.rate || 0,
        discount: item.discount || 0,
        discount_type: item.discountType || 'flat',
        gst_rate: item.gstRate || 0,
        gst_type: item.gstType || 'none',
        hsn: item.hsn || '',
        sac: item.sac || '',
        uom: item.uom || 'unit',
        source_item_id: item.sourceItemId,
        is_overridden: item.isOverridden || false,
      })),
      notes: data.notes || currentQuotation.notes || '',
      status: data.status || currentQuotation.status,
      ...calculations,
    };

    await databaseService.updateDocument(updatedQuotation);
    
    return {
      ...updatedQuotation,
      id: updatedQuotation._id,
    };
  },

  /**
   * Calculate quotation totals
   */
  calculateTotals(items, options = {}) {
    let subtotal = 0;
    let taxTotal = 0;

    items.forEach(item => {
      const quantity = item.quantity || 0;
      const rate = item.rate || 0;
      const itemDiscount = item.discount || 0;
      const itemDiscountType = item.discountType || 'flat';
      
      // Calculate item amount before tax
      let itemAmount = quantity * rate;
      
      // Apply item discount
      if (itemDiscountType === 'percentage') {
        itemAmount -= (itemAmount * itemDiscount) / 100;
      } else {
        itemAmount -= itemDiscount;
      }
      
      subtotal += itemAmount;
      
      // Calculate tax
      const gstRate = item.gstRate || 0;
      const itemTax = (itemAmount * gstRate) / 100;
      taxTotal += itemTax;
    });

    // Apply quotation-level discount
    const discount = options.discount || 0;
    const discountType = options.discountType || 'flat';
    
    if (discountType === 'percentage') {
      subtotal -= (subtotal * discount) / 100;
    } else {
      subtotal -= discount;
    }

    // Add shipping
    const shipping = options.shipping || 0;
    subtotal += shipping;

    // Calculate total
    let total = subtotal + taxTotal;

    // Apply round off
    const roundOff = options.roundOff || 0;
    total += roundOff;

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      tax_total: Math.round(taxTotal * 100) / 100,
      total: Math.round(total * 100) / 100,
    };
  },

  /**
   * Send quotation (finalize and lock)
   */
  async send(quotationId, data = {}) {
    const currentQuotation = await databaseService.readDocument(quotationId);
    
    if (!currentQuotation) {
      throw new Error('Quotation not found');
    }

    if (currentQuotation.status !== 'draft') {
      throw new Error('Only draft quotations can be sent');
    }

    // Validate quotation
    if (!currentQuotation.client_id) {
      throw new Error('Client is required');
    }

    if (!currentQuotation.items || currentQuotation.items.length === 0) {
      throw new Error('At least one line item is required');
    }

    // Update status to sent
    const updatedQuotation = {
      ...currentQuotation,
      status: 'sent',
      sent_at: new Date().toISOString(),
    };

    await databaseService.updateDocument(updatedQuotation);

    // Try to send email
    let emailResult = {
      skipped: true,
      message: 'Email functionality not yet implemented in web version'
    };

    try {
      console.log('[Quotation Service] Attempting to send email for quotation:', quotationId);
      
      // Import email service dynamically to avoid circular dependencies
      const { sendQuotationEmail } = await import('./email.service');
      
      // Get company settings and theme for email and PDF
      const settingsService = await import('./settings.service');
      const theme = await settingsService.default.getActiveTheme();
      const companySettings = theme?.company || {};
      
      console.log('[Quotation Service] Quotation data:', {
        id: updatedQuotation._id,
        quote_number: updatedQuotation.quote_number,
        client_id: updatedQuotation.client_id,
        client_name: updatedQuotation.client_name,
        client_email: updatedQuotation.client_email
      });
      
      console.log('[Quotation Service] Backend will generate PDF automatically');
      
      // Send email - backend will generate PDF attachment
      const result = await sendQuotationEmail(
        {
          ...updatedQuotation,
          id: updatedQuotation._id,
          client: {
            email: updatedQuotation.client_email,
            name: updatedQuotation.client_name
          },
          quoteNumber: updatedQuotation.quote_number,
          quoteDate: updatedQuotation.quote_date,
          validUntil: updatedQuotation.valid_until,
          total: updatedQuotation.total
        },
        companySettings,
        null // No PDF base64 needed - backend generates it
      );
      
      console.log('[Quotation Service] Email send result:', result);
      emailResult = result;
    } catch (emailError) {
      console.error('[Quotation Service] Email sending failed:', emailError);
      emailResult = {
        success: false,
        message: emailError.message || 'Failed to send email'
      };
    }
    
    return {
      ...updatedQuotation,
      id: updatedQuotation._id,
      status: 'sent',
      emailResult
    };
  },

  /**
   * Resend quotation (send email again for already sent quotation)
   */
  async resend(quotationId) {
    const currentQuotation = await databaseService.readDocument(quotationId);
    
    if (!currentQuotation) {
      throw new Error('Quotation not found');
    }

    // Validate quotation must be sent or accepted to resend
    if (currentQuotation.status !== 'sent' && currentQuotation.status !== 'accepted') {
      throw new Error('Only sent or accepted quotations can be resent');
    }

    // Validate quotation has client
    if (!currentQuotation.client_id) {
      throw new Error('Client is required to resend quotation');
    }

    console.log('[Quotation Service] Resending quotation:', quotationId);

    // Try to send email
    let emailResult = {
      skipped: true,
      message: 'Email functionality not yet implemented in web version'
    };

    try {
      console.log('[Quotation Service] Attempting to resend email for quotation:', quotationId);
      
      // Import email service dynamically to avoid circular dependencies
      const { sendQuotationEmail } = await import('./email.service');
      
      // Get company settings and theme for email and PDF
      const settingsService = await import('./settings.service');
      const theme = await settingsService.default.getActiveTheme();
      const companySettings = theme?.company || {};
      
      console.log('[Quotation Service] Quotation data for resend:', {
        id: currentQuotation._id,
        quote_number: currentQuotation.quote_number,
        client_id: currentQuotation.client_id,
        client_name: currentQuotation.client_name,
        client_email: currentQuotation.client_email
      });
      
      console.log('[Quotation Service] Backend will generate PDF automatically');
      
      // Send email - backend will generate PDF attachment
      const result = await sendQuotationEmail(
        {
          ...currentQuotation,
          id: currentQuotation._id,
          client: {
            email: currentQuotation.client_email,
            name: currentQuotation.client_name
          },
          quoteNumber: currentQuotation.quote_number,
          quoteDate: currentQuotation.quote_date,
          validUntil: currentQuotation.valid_until,
          total: currentQuotation.total
        },
        companySettings,
        null // No PDF base64 needed - backend generates it
      );
      
      console.log('[Quotation Service] Email resend result:', result);
      emailResult = result;
    } catch (emailError) {
      console.error('[Quotation Service] Email resending failed:', emailError);
      emailResult = {
        success: false,
        message: emailError.message || 'Failed to resend email'
      };
    }
    
    return {
      ...currentQuotation,
      id: currentQuotation._id,
      emailResult
    };
  },

  /**
   * Get quotation by ID
   */
  async get(id) {
    const quotation = await databaseService.readDocument(id);
    
    if (!quotation) {
      return null;
    }

    return {
      ...quotation,
      id: quotation._id,
    };
  },

  /**
   * List all quotations with optional filters
   */
  async list(filters = {}) {
    const quotations = await databaseService.getQuotations();
    
    // Apply filters
    let filtered = quotations;
    
    // Filter by archived status
    if (filters.archived !== undefined) {
      if (filters.archived) {
        // Show only archived quotations
        filtered = filtered.filter(quot => quot.archived_at);
      } else {
        // Show only non-archived quotations
        filtered = filtered.filter(quot => !quot.archived_at);
      }
    }
    
    // Filter by status (if not "all")
    if (filters.status && filters.status !== 'all') {
      filtered = filtered.filter(quot => quot.status === filters.status);
    }
    
    // Filter by client
    if (filters.client_id) {
      filtered = filtered.filter(quot => quot.client_id === filters.client_id);
    }
    
    // Filter by search query
    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(quot => 
        (quot.quote_number || '').toLowerCase().includes(search) ||
        (quot.client_name || '').toLowerCase().includes(search)
      );
    }

    // Sort
    const sortBy = filters.sortBy || 'updated_at';
    const sortOrder = filters.sortOrder || 'DESC';
    
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'quote_number':
          aVal = a.quote_number || '';
          bVal = b.quote_number || '';
          break;
        case 'client_name':
          aVal = a.client_name || '';
          bVal = b.client_name || '';
          break;
        case 'total':
          aVal = a.total || 0;
          bVal = b.total || 0;
          break;
        case 'issue_date':
        case 'quote_date':
          aVal = new Date(a.issue_date || a.quote_date || 0);
          bVal = new Date(b.issue_date || b.quote_date || 0);
          break;
        case 'updated_at':
        default:
          aVal = new Date(a.metadata?.updatedAt || a.metadata?.createdAt || 0);
          bVal = new Date(b.metadata?.updatedAt || b.metadata?.createdAt || 0);
          break;
      }
      
      if (sortOrder === 'ASC') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered.map(quot => ({
      ...quot,
      id: quot._id,
      updated_at: quot.metadata?.updatedAt || quot.metadata?.createdAt,
      created_at: quot.metadata?.createdAt,
    }));
  },

  /**
   * Delete quotation (draft only)
   */
  async delete(id) {
    const quotation = await databaseService.readDocument(id);
    
    if (!quotation) {
      throw new Error('Quotation not found');
    }

    if (quotation.status !== 'draft') {
      throw new Error('Only draft quotations can be deleted');
    }

    await databaseService.deleteDocument(id);
    
    return { success: true };
  },

  /**
   * Archive quotation
   */
  async archive(id) {
    const currentQuotation = await databaseService.readDocument(id);
    
    if (!currentQuotation) {
      throw new Error('Quotation not found');
    }

    const archivedQuotation = {
      ...currentQuotation,
      archived_at: new Date().toISOString(),
    };

    await databaseService.updateDocument(archivedQuotation);
    
    return {
      ...archivedQuotation,
      id: archivedQuotation._id,
    };
  },

  /**
   * Unarchive quotation
   */
  async unarchive(id) {
    const currentQuotation = await databaseService.readDocument(id);
    
    if (!currentQuotation) {
      throw new Error('Quotation not found');
    }

    const unarchivedQuotation = {
      ...currentQuotation,
      archived_at: null,
    };

    await databaseService.updateDocument(unarchivedQuotation);
    
    return {
      ...unarchivedQuotation,
      id: unarchivedQuotation._id,
    };
  },

  /**
   * Delete archived quotation (permanent)
   */
  async deleteArchived(id) {
    await databaseService.deleteDocument(id);
    return { success: true };
  },

  /**
   * Convert quotation to invoice
   */
  async convert(quotationId) {
    const quotation = await this.get(quotationId);
    
    if (!quotation) {
      throw new Error('Quotation not found');
    }

    // Import invoice service to avoid circular dependency
    const { invoiceService } = await import('./invoice.service.js');
    
    // Create new invoice from quotation
    const invoice = await invoiceService.create({
      type: quotation.quotationType,
    });

    // Copy quotation data to invoice
    const invoiceData = {
      client: {
        id: quotation.client_id,
        name: quotation.client_name,
        email: quotation.client_email,
        phone: quotation.client_phone,
        gstin: quotation.client_gstin,
        billing_address: quotation.client_billing_address,
        city: quotation.client_city,
        state: quotation.client_state,
        pincode: quotation.client_pincode,
      },
      items: quotation.items,
      discount: quotation.discount,
      discountType: quotation.discount_type,
      shipping: quotation.shipping,
      roundOff: quotation.round_off,
      notes: quotation.notes,
    };

    await invoiceService.save(invoice.id, invoiceData);

    // Mark quotation as converted
    const updatedQuotation = {
      ...quotation,
      status: 'converted',
      converted_to_invoice_id: invoice.id,
      converted_at: new Date().toISOString(),
    };

    await databaseService.updateDocument(updatedQuotation);

    return {
      invoice,
      quotation: updatedQuotation,
    };
  },

  /* ═══════════════════════════════════════════════════════════
     LEGACY METHODS (for backward compatibility)
     ═══════════════════════════════════════════════════════════ */

  async add(data) {
    return this.create(data);
  },

  async update(id, data) {
    return this.save(id, data);
  },

  async updateStatus(id, status) {
    const quotation = await databaseService.readDocument(id);
    
    if (!quotation) {
      throw new Error('Quotation not found');
    }

    const updatedQuotation = {
      ...quotation,
      status: status,
    };

    await databaseService.updateDocument(updatedQuotation);
    
    return {
      ...updatedQuotation,
      id: updatedQuotation._id,
    };
  },

  /* ═══════════════════════════════════════════════════════════
     ITEMS
     ═══════════════════════════════════════════════════════════ */

  async saveItems(quotationId, items, options) {
    const quotation = await this.get(quotationId);
    
    if (!quotation) {
      throw new Error('Quotation not found');
    }

    return this.save(quotationId, {
      items,
      discount: options?.discount,
      discountType: options?.discountType,
      shipping: options?.shipping,
      roundOff: options?.roundOff,
    });
  },

  async getItems(quotationId) {
    const quotation = await this.get(quotationId);
    return quotation?.items || [];
  },

  /* ═══════════════════════════════════════════════════════════
     PDF / PRINT (Not implemented for web MVP)
     ═══════════════════════════════════════════════════════════ */

  async print(quotation) {
    // TODO: Implement browser-based PDF generation
    // Options:
    // 1. Use jsPDF or pdfmake
    // 2. Call backend API to generate PDF
    // 3. Use html2pdf to convert preview to PDF
    throw new Error('PDF generation not yet implemented in web version');
  },
};
